import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { AppLayout } from "../components/tabon/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  Download, Lock, Unlock, RefreshCw, Plus, Trash2, ChevronDown, ChevronRight, BedDouble,
} from "lucide-react";

const money = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const todayIST = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
      hour12: true, timeZone: "Asia/Kolkata",
    });
  } catch { return iso; }
};
const PM_LABEL = { cash: "Cash", online: "Online", upi: "UPI", card: "Card", dineout: "Dineout", district: "District", eazydiner: "EazyDiner", unknown: "Unknown" };

async function downloadCsv(path, params, filename) {
  try {
    const resp = await api.get(path, { params, responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(formatError(err));
  }
}

// ── Small building blocks ─────────────────────────────────────────────────────
function StatCard({ label, value, accent = "text-white" }) {
  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wider text-[#A0A0A0] mb-1">{label}</div>
      <div className={`text-2xl font-bold ${accent}`}>{value}</div>
    </div>
  );
}

function Panel({ title, children, right }) {
  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold uppercase tracking-wider text-white">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

// ── Cash + Close Day panel ────────────────────────────────────────────────────
function CashClosePanel({ data, date, onChanged }) {
  const { user } = useAuth();
  const cash = data.cash || {};
  const closed = data.day_close;
  const [openingFloat, setOpeningFloat] = useState(String(user?.venue?.opening_float_default ?? 0));
  const [countedCash, setCountedCash] = useState("");
  const [busy, setBusy] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payNote, setPayNote] = useState("");
  const [payouts, setPayouts] = useState([]);

  const loadPayouts = useCallback(async () => {
    try {
      const { data: rows } = await api.get("/reports/cash-payouts", { params: { date } });
      setPayouts(rows || []);
    } catch { /* ignore */ }
  }, [date]);

  useEffect(() => { loadPayouts(); }, [loadPayouts]);

  const liveExpected = Number(openingFloat || 0) + Number(cash.cash_sales || 0) - Number(cash.payouts_total || 0);
  const liveVariance = Number(countedCash || 0) - liveExpected;

  const addPayout = async () => {
    const amt = Number(payAmount);
    if (!amt || amt <= 0) { toast.error("Enter a payout amount"); return; }
    setBusy(true);
    try {
      await api.post("/reports/cash-payout", { business_date: date, amount: amt, note: payNote.trim() });
      setPayAmount(""); setPayNote("");
      await loadPayouts();
      onChanged();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const removePayout = async (id) => {
    try {
      await api.delete(`/reports/cash-payout/${id}`);
      await loadPayouts();
      onChanged();
    } catch (err) { toast.error(formatError(err)); }
  };

  const closeDay = async () => {
    if (!window.confirm(`Close ${date}? This locks the cash count for the day.`)) return;
    setBusy(true);
    try {
      await api.post("/reports/day-close", {
        business_date: date,
        opening_float: Number(openingFloat || 0),
        counted_cash: Number(countedCash || 0),
      });
      toast.success("Day closed");
      onChanged();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const reopenDay = async () => {
    if (!window.confirm(`Reopen ${date}? The cash count will be cleared so you can re-close it. This is logged.`)) return;
    setBusy(true);
    try {
      await api.post(`/reports/day-close/${date}/reopen`);
      toast.success("Day reopened");
      onChanged();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Panel title="Cash drawer & day close">
      {/* Cash figures */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div><div className="text-xs text-[#A0A0A0]">Opening float</div><div className="text-lg font-bold text-white">{money(closed ? closed.opening_float : cash.opening_float)}</div></div>
        <div>
          <div className="text-xs text-[#A0A0A0]">Cash sales</div>
          <div className="text-lg font-bold text-white">{money(cash.cash_sales)}</div>
          {Number(cash.pending_collection_cash) > 0 && (
            <div className="text-[10px] text-[#A0A0A0] mt-0.5" title="Cash taken on orders paid but not yet handed over — in the drawer, but not in Net Collected">
              incl. {money(cash.pending_collection_cash)} taken, awaiting handover
            </div>
          )}
        </div>
        <div><div className="text-xs text-[#A0A0A0]">Payouts</div><div className="text-lg font-bold text-[#F59E0B]">−{money(cash.payouts_total)}</div></div>
        <div className="bg-[#9D00FF]/10 border border-[#9D00FF]/30 rounded-lg px-3 py-2">
          <div className="text-xs text-[#D78CFF]">Expected in drawer</div>
          <div className="text-lg font-bold text-white">{money(closed ? closed.expected_cash : cash.expected_cash)}</div>
        </div>
      </div>

      {/* Payout form + list */}
      <div className="border-t border-white/5 pt-3 mb-4">
        <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2">Record cash payout</div>
        <div className="flex gap-2 flex-wrap items-center">
          <input type="number" min="0" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder="Amount"
            disabled={!!closed} className="w-28 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-50" />
          <input value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="Note (e.g. vendor payment)"
            disabled={!!closed} className="flex-1 min-w-[160px] bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-50" />
          <button type="button" onClick={addPayout} disabled={busy || !!closed}
            className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-40 rounded-lg px-3 py-2 text-xs font-semibold">
            <Plus size={13} /> Add
          </button>
        </div>
        {payouts.length > 0 && (
          <div className="mt-2 flex flex-col gap-1">
            {payouts.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-xs bg-[#0F0F0F] border border-white/5 rounded-lg px-3 py-1.5">
                <span className="text-white font-semibold">{money(p.amount)}</span>
                <span className="text-[#A0A0A0] truncate flex-1 mx-3">{p.note || "—"}</span>
                {!closed && (
                  <button type="button" onClick={() => removePayout(p.id)} className="text-[#EF4444] hover:text-red-300"><Trash2 size={13} /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Close / closed state */}
      {closed ? (
        <div className="border-t border-white/5 pt-3">
          <div className="flex items-center gap-2 mb-2">
            <Lock size={14} className="text-emerald-400" />
            <span className="text-sm font-bold text-white">Day closed</span>
            <span className="text-xs text-[#A0A0A0]">· {fmtDateTime(closed.closed_at)}{closed.closed_by_name ? ` · by ${closed.closed_by_name}` : ""}</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div><div className="text-xs text-[#A0A0A0]">Expected</div><div className="text-base font-bold text-white">{money(closed.expected_cash)}</div></div>
            <div><div className="text-xs text-[#A0A0A0]">Counted</div><div className="text-base font-bold text-white">{money(closed.counted_cash)}</div></div>
            <div>
              <div className="text-xs text-[#A0A0A0]">Variance</div>
              <div className={`text-base font-bold ${Number(closed.variance) === 0 ? "text-emerald-400" : "text-[#F59E0B]"}`}>
                {Number(closed.variance) > 0 ? "+" : ""}{money(closed.variance)}
              </div>
            </div>
          </div>
          <button type="button" onClick={reopenDay} disabled={busy}
            className="flex items-center gap-1.5 border border-white/10 text-[#A0A0A0] hover:text-white hover:border-white/30 rounded-lg px-3 py-2 text-xs font-semibold disabled:opacity-50">
            <Unlock size={13} /> Reopen day
          </button>
        </div>
      ) : (
        <div className="border-t border-white/5 pt-3">
          <div className="flex gap-3 flex-wrap items-end">
            <div>
              <label className="text-xs text-[#A0A0A0] block mb-1">Opening float</label>
              <input type="number" min="0" value={openingFloat} onChange={(e) => setOpeningFloat(e.target.value)}
                className="w-32 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-[#A0A0A0] block mb-1">Counted cash</label>
              <input type="number" min="0" value={countedCash} onChange={(e) => setCountedCash(e.target.value)}
                className="w-32 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <div className="text-xs text-[#A0A0A0] mb-1">Variance</div>
              <div className={`text-lg font-bold ${liveVariance === 0 ? "text-emerald-400" : "text-[#F59E0B]"}`}>
                {countedCash === "" ? "—" : `${liveVariance > 0 ? "+" : ""}${money(liveVariance)}`}
              </div>
            </div>
            <button type="button" onClick={closeDay} disabled={busy || countedCash === ""}
              className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white font-semibold rounded-lg px-5 py-2 text-sm">
              Close Day
            </button>
          </div>
        </div>
      )}
    </Panel>
  );
}

// ── Day Summary tab ───────────────────────────────────────────────────────────
function DaySummaryTab() {
  // The EOD WhatsApp report links here as /reports?date=YYYY-MM-DD (J-5). Open on that day when the
  // param is a well-formed date, otherwise today — never pass an unvalidated value to the API.
  const [searchParams] = useSearchParams();
  const paramDate = searchParams.get("date");
  const [date, setDate] = useState(
    paramDate && /^\d{4}-\d{2}-\d{2}$/.test(paramDate) ? paramDate : todayIST()
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/day-summary", { params: { date } });
      setData(d);
    } catch (err) { toast.error(formatError(err)); setData(null); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 flex-wrap">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
        <button type="button" onClick={load} className="flex items-center gap-1.5 border border-white/10 text-[#A0A0A0] hover:text-white hover:border-white/30 rounded-lg px-3 py-2 text-xs"><RefreshCw size={12} /> Refresh</button>
        {data?.source === "ordering" && (
          <button type="button" onClick={() => downloadCsv("/reports/export/orders.csv", { from: date, to: date }, `tabon-orders-${date}.csv`)}
            className="ml-auto flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold"><Download size={12} /> Orders CSV</button>
        )}
      </div>

      {loading ? (
        <div className="text-[#A0A0A0] text-sm py-16 text-center">Loading…</div>
      ) : !data ? (
        <div className="text-[#A0A0A0] text-sm py-16 text-center">No data.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatCard label={`Net Collected${data.tax ? " (incl. tax)" : ""}`} value={money(data.sales.net_collected)} accent="text-emerald-400" />
            <StatCard label="Order Count" value={data.sales.order_count} />
            <StatCard label="Avg Order Value" value={money(data.sales.avg_order_value)} />
          </div>

          {Number(data.sales.points_redeemed_value) > 0 && (
            <Panel title="Points redeemed">
              <div className="text-lg font-bold text-white">{money(data.sales.points_redeemed_value)}</div>
              <div className="text-xs text-[#A0A0A0] mt-1">Gross sales {money(data.sales.gross_sales)} · Net collected {money(data.sales.net_collected)}</div>
            </Panel>
          )}

          {data.cash && data.day_close_enabled && data.source === "ordering" && (
            <CashClosePanel key={date} data={data} date={date} onChanged={load} />
          )}
          {data.cash && !data.day_close_enabled && (
            <Panel title="Cash drawer">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><div className="text-xs text-[#A0A0A0]">Opening float</div><div className="text-lg font-bold text-white">{money(data.cash.opening_float)}</div></div>
                <div>
                  <div className="text-xs text-[#A0A0A0]">Cash sales</div>
                  <div className="text-lg font-bold text-white">{money(data.cash.cash_sales)}</div>
                  {Number(data.cash.pending_collection_cash) > 0 && (
                    <div className="text-[10px] text-[#A0A0A0] mt-0.5" title="Cash taken on orders paid but not yet handed over — in the drawer, but not in Net Collected">
                      incl. {money(data.cash.pending_collection_cash)} taken, awaiting handover
                    </div>
                  )}
                </div>
                <div><div className="text-xs text-[#A0A0A0]">Payouts</div><div className="text-lg font-bold text-[#F59E0B]">−{money(data.cash.payouts_total)}</div></div>
                <div className="bg-[#9D00FF]/10 border border-[#9D00FF]/30 rounded-lg px-3 py-2"><div className="text-xs text-[#D78CFF]">Expected</div><div className="text-lg font-bold text-white">{money(data.cash.expected_cash)}</div></div>
              </div>
              <p className="text-[11px] text-[#A0A0A0] mt-2">Enable Close Day in Settings to record a cash count and variance.</p>
            </Panel>
          )}

          {data.payment_breakdown && Object.keys(data.payment_breakdown).length > 0 && (
            <Panel title="Payment breakdown">
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(data.payment_breakdown).map(([pm, v]) => (
                    <tr key={pm} className="border-b border-white/5 last:border-0">
                      <td className="py-1.5 text-white">
                        {PM_LABEL[pm] || pm}
                        {pm === "online" && (
                          <span className="ml-2 align-middle text-[10px] uppercase tracking-wider text-sky-300/90 bg-sky-500/10 border border-sky-500/30 rounded px-1.5 py-0.5">settling to the bank</span>
                        )}
                      </td>
                      <td className="py-1.5 text-[#A0A0A0] text-right">{v.count} orders</td>
                      <td className="py-1.5 text-white font-semibold text-right">{money(v.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.payment_breakdown.online && (
                // J-4c: online is not cash in the drawer — it is the GROSS amount settling to the bank.
                // We deliberately do NOT net out gateway fees: TabOn doesn't know the venue's MDR plan,
                // and a wrong net figure is worse than none.
                <p className="text-[11px] text-[#A0A0A0] mt-2 leading-relaxed">
                  Online is the gross amount settling to your bank account — not cash in the drawer. Your
                  payment gateway deducts its fees before payout, so the actual bank credit is lower; TabOn
                  doesn't know your fee plan and doesn't net it out here.
                </p>
              )}
            </Panel>
          )}

          {data.order_type_split && Object.keys(data.order_type_split).length > 0 && (
            <Panel title="Order type">
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(data.order_type_split).map(([ot, v]) => (
                  <div key={ot}>
                    <div className="text-xs text-[#A0A0A0] capitalize">{ot.replace("_", "-")}</div>
                    <div className="text-base font-bold text-white">{money(v.amount)}</div>
                    <div className="text-[11px] text-[#555]">{v.count} orders</div>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          {data.tax && (
            <Panel title="Tax (GST)">
              <div className="grid grid-cols-3 gap-3">
                <div><div className="text-xs text-[#A0A0A0]">Taxable value</div><div className="text-base font-bold text-white">{money(data.tax.taxable_value)}</div></div>
                <div><div className="text-xs text-[#A0A0A0]">GST</div><div className="text-base font-bold text-white">{money(data.tax.gst_amount)}</div></div>
                <div><div className="text-xs text-[#A0A0A0]">Round off</div><div className="text-base font-bold text-white">{money(data.tax.round_off)}</div></div>
              </div>
              {/* Z-1: the identity now holds — taxable value is the base GST was charged on (net of
                  discounts + loyalty redemption), the same base compute_bill used, so it ties to GST. */}
              <p className="text-[11px] text-[#A0A0A0] mt-3 leading-relaxed">
                Taxable value + GST + round-off = the Net Collected above. Taxable value is the base GST
                was actually charged on — <span className="text-[#C0C0C0]">after discounts and loyalty
                redemption</span>. The Item Sales report shows gross item sales (before tax, discounts and
                redemption), so it reads higher than this taxable value by exactly those.
              </p>
            </Panel>
          )}

          {/* J-4a: "Open (uncollected)" conflated two opposites, so it is split. unpaid_orders = money
              still at risk (running/awaiting payment); pending_collection = money already captured,
              waiting to be handed over. The deprecated open_orders (their sum) is no longer shown. */}
          {(data.unpaid_orders || data.pending_collection || data.cancelled || data.unpaid) && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {data.unpaid_orders && (
                <Panel title="At risk (unpaid)">
                  <div className="text-lg font-bold text-[#F59E0B]">{money(data.unpaid_orders.value)}</div>
                  <div className="text-xs text-[#A0A0A0]">{data.unpaid_orders.count} open, not yet paid</div>
                </Panel>
              )}
              {data.pending_collection && (
                <Panel title="Pending collection">
                  <div className="text-lg font-bold text-white">{money(data.pending_collection.value)}</div>
                  <div className="text-xs text-[#A0A0A0]">{data.pending_collection.count} paid, awaiting handover</div>
                </Panel>
              )}
              {data.cancelled && (
                <Panel title="Cancelled">
                  <div className="text-lg font-bold text-[#EF4444]">{money(data.cancelled.value)}</div>
                  <div className="text-xs text-[#A0A0A0]">{data.cancelled.count} cancelled</div>
                </Panel>
              )}
              {data.unpaid && (
                <Panel title="Unpaid (closed)">
                  <div className="text-lg font-bold text-[#F59E0B]">{money(data.unpaid.value)}</div>
                  <div className="text-xs text-[#A0A0A0]">{data.unpaid.count} closed, no payment</div>
                </Panel>
              )}
            </div>
          )}

          {data.by_staff && data.by_staff.length > 0 && (
            <Panel title="By staff">
              <table className="w-full text-sm">
                <tbody>
                  {data.by_staff.map((s) => (
                    <tr key={s.staff_id || s.staff_name} className="border-b border-white/5 last:border-0">
                      <td className="py-1.5 text-white">{s.staff_name}</td>
                      <td className="py-1.5 text-[#A0A0A0] text-right">{s.count} orders</td>
                      <td className="py-1.5 text-white font-semibold text-right">{money(s.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )}

          {data.exceptions && <ExceptionsSection exceptions={data.exceptions} />}
        </>
      )}
    </div>
  );
}

// Exceptions — what staff gave away or changed today, read from check_events. Each row expands to
// the individual events (time, item/bill, amount, reason, staff).
function ExceptionsSection({ exceptions }) {
  const ROWS = [
    ["items_cancelled_after_kot", "Items cancelled after KOT", true],
    ["items_made_free", "Items made free", true],
    ["discounts_given", "Discounts given", true],
    ["bills_voided", "Bills voided", true],
    ["bills_reopened", "Bills reopened", false],
    ["bills_marked_unpaid", "Bills marked unpaid", true],
  ];
  const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
  const anything = ROWS.some(([k]) => (exceptions[k]?.count || 0) > 0);
  return (
    <Panel title="Exceptions">
      {!anything && <div className="text-xs text-[#A0A0A0]">Nothing given away or changed today.</div>}
      <div className="flex flex-col divide-y divide-white/5">
        {ROWS.map(([key, label, hasValue]) => {
          const b = exceptions[key] || { count: 0, events: [] };
          if (!b.count) return null;
          return (
            <details key={key} className="py-1">
              <summary className="cursor-pointer flex items-center justify-between text-sm text-white py-1.5">
                <span>{label}</span>
                <span className="text-[#A0A0A0]">{b.count}{hasValue ? ` · ${money(b.value)}` : ""}</span>
              </summary>
              <div className="pb-2">
                {(b.events || []).map((e) => (
                  <div key={e.id} className="flex items-start justify-between gap-3 text-xs text-[#A0A0A0] py-1 pl-2">
                    <span className="whitespace-nowrap">{fmtTime(e.created_at)}</span>
                    <span className="flex-1 min-w-0">
                      <span className="text-[#C0C0C0]">{e.item_name || "Bill"}</span>
                      {e.reason ? ` — ${e.reason}` : ""}
                      {e.override_used ? " · PIN" : ""}
                      <span className="block text-[10px] text-[#666]">{e.actor_name}</span>
                    </span>
                    {hasValue && <span className="text-[#C0C0C0] whitespace-nowrap">{money(e.amount || 0)}</span>}
                  </div>
                ))}
              </div>
            </details>
          );
        })}
      </div>
    </Panel>
  );
}

// ── Item Sales tab ────────────────────────────────────────────────────────────
function ItemSalesTab() {
  const [from, setFrom] = useState(todayIST());
  const [to, setTo] = useState(todayIST());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("revenue"); // "revenue" | "qty"
  const [showNonMovers, setShowNonMovers] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/item-sales", { params: { from, to } });
      setData(d);
    } catch (err) { toast.error(formatError(err)); setData(null); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  // Group items by category (preserving category order from data.categories)
  const itemsByCat = {};
  (data?.items || []).forEach((it) => {
    (itemsByCat[it.category] = itemsByCat[it.category] || []).push(it);
  });
  Object.values(itemsByCat).forEach((arr) => arr.sort((a, b) => (sortBy === "qty" ? b.qty - a.qty : b.revenue - a.revenue)));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
        <span className="text-[#A0A0A0] text-sm">to</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
        <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg p-0.5 ml-1">
          {["revenue", "qty"].map((k) => (
            <button key={k} type="button" onClick={() => setSortBy(k)}
              className={`px-3 py-1 rounded-md text-xs font-semibold transition ${sortBy === k ? "bg-[#9D00FF]/30 text-white" : "text-[#A0A0A0] hover:text-white"}`}>
              {k === "revenue" ? "By revenue" : "By quantity"}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => downloadCsv("/reports/export/items.csv", { from, to }, `tabon-items-${from}-to-${to}.csv`)}
          className="ml-auto flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold"><Download size={12} /> Items CSV</button>
      </div>

      {loading ? (
        <div className="text-[#A0A0A0] text-sm py-16 text-center">Loading…</div>
      ) : !data ? (
        <div className="text-[#A0A0A0] text-sm py-16 text-center">No data.</div>
      ) : (
        <>
          <StatCard label="Total Revenue (pre-tax)" value={money(data.total_revenue)} accent="text-emerald-400" />
          {/* J-4b: item revenue is menu price × qty, before GST/discounts/round-off — flagged pre-tax so
              it is not read as the tax-inclusive money in Day Summary, which is a different (higher) basis. */}
          <p className="text-[11px] text-[#A0A0A0] -mt-1">
            Pre-tax: menu price × quantity, before GST, discounts and round-off — so this won't match the
            tax-inclusive totals in Day Summary.
          </p>
          {(data.categories || []).map((cat) => (
            <Panel key={cat.category} title={cat.category}
              right={<span className="text-xs text-[#A0A0A0]">{cat.qty} sold · <span className="text-white font-semibold">{money(cat.revenue)}</span></span>}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#555]">
                    <th className="text-left font-semibold pb-1">Item</th>
                    <th className="text-right font-semibold pb-1">Qty</th>
                    <th className="text-right font-semibold pb-1">Revenue</th>
                    <th className="text-right font-semibold pb-1">%</th>
                  </tr>
                </thead>
                <tbody>
                  {(itemsByCat[cat.category] || []).map((it) => (
                    <tr key={it.item_id || it.name} className="border-b border-white/5 last:border-0">
                      <td className="py-1.5 text-white">{it.name}</td>
                      <td className="py-1.5 text-[#A0A0A0] text-right">{it.qty}</td>
                      <td className="py-1.5 text-white font-semibold text-right">{money(it.revenue)}</td>
                      <td className="py-1.5 text-[#555] text-right">{it.pct_of_revenue}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          ))}
          {(data.items || []).length === 0 && (
            <div className="text-[#A0A0A0] text-sm py-8 text-center">No sales in this period.</div>
          )}

          {(data.non_movers || []).length > 0 && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5">
              <button type="button" onClick={() => setShowNonMovers((v) => !v)} className="flex items-center gap-2 w-full text-left">
                {showNonMovers ? <ChevronDown size={16} className="text-[#A0A0A0]" /> : <ChevronRight size={16} className="text-[#A0A0A0]" />}
                <span className="text-sm font-bold uppercase tracking-wider text-white">Not sold in this period</span>
                <span className="text-xs text-[#A0A0A0]">({data.non_movers.length})</span>
              </button>
              {showNonMovers && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {data.non_movers.map((it) => (
                    <span key={it.item_id} className="text-xs bg-[#0F0F0F] border border-white/5 rounded-lg px-2.5 py-1 text-[#A0A0A0]">
                      {it.name} <span className="text-[#555]">· {it.category}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Room Service tab ──────────────────────────────────────────────────────────
function RoomServiceTab() {
  const [from, setFrom] = useState(todayIST());
  const [to, setTo] = useState(todayIST());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/room-service", { params: { from, to } });
      setData(d);
    } catch (err) { toast.error(formatError(err)); setData(null); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
        <span className="text-[#A0A0A0] text-sm">to</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
        <button type="button" onClick={load} className="flex items-center gap-1.5 border border-white/10 text-[#A0A0A0] hover:text-white hover:border-white/30 rounded-lg px-3 py-2 text-xs"><RefreshCw size={12} /> Refresh</button>
      </div>

      {loading ? (
        <div className="text-[#A0A0A0] text-sm py-16 text-center">Loading…</div>
      ) : !data ? (
        <div className="text-[#A0A0A0] text-sm py-16 text-center">No data.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatCard label="Room Revenue (settled)" value={money(data.settled.revenue)} accent="text-emerald-400" />
            <StatCard label="Orders" value={data.settled.order_count} />
            <StatCard label="Avg Order Value" value={money(data.settled.avg_order_value)} />
          </div>

          {data.settled.payment_breakdown && Object.keys(data.settled.payment_breakdown).length > 0 && (
            <Panel title="Payment breakdown">
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(data.settled.payment_breakdown).map(([pm, v]) => (
                    <tr key={pm} className="border-b border-white/5 last:border-0">
                      <td className="py-1.5 text-white">{PM_LABEL[pm] || pm}</td>
                      <td className="py-1.5 text-[#A0A0A0] text-right">{v.count} orders</td>
                      <td className="py-1.5 text-white font-semibold text-right">{money(v.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )}

          <Panel title="Outstanding (delivered, unsettled)"
            right={<span className="text-sm font-bold text-[#F59E0B]">{money(data.outstanding_total)} owed</span>}>
            {data.outstanding.length === 0 ? (
              <div className="text-[#A0A0A0] text-sm py-4 text-center">Nothing outstanding — all room orders settled.</div>
            ) : (
              <div className="flex flex-col gap-2">
                {data.outstanding.map((r) => (
                  <div key={r.room_number} className="flex items-center gap-3 bg-[#0F0F0F] border border-white/5 rounded-lg px-3 py-2">
                    <span className="flex items-center gap-1.5 text-xs bg-sky-500/15 text-sky-300 border border-sky-500/30 px-2 py-0.5 rounded-full">
                      <BedDouble size={11} /> Room {r.room_number}
                    </span>
                    <span className="text-xs text-[#A0A0A0]">{r.order_count} order{r.order_count !== 1 ? "s" : ""}</span>
                    <span className="text-xs text-[#555]">since {fmtDateTime(r.oldest_order_at)}</span>
                    <span className="ml-auto text-sm font-bold text-white">{money(r.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}

// ── Redemptions tab ───────────────────────────────────────────────────────────
function RedemptionsTab() {
  const [from, setFrom] = useState(todayIST());
  const [to, setTo] = useState(todayIST());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/redemptions", { params: { from, to } });
      setData(d);
    } catch (err) { toast.error(formatError(err)); setData(null); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
        <span className="text-[#A0A0A0] text-sm">to</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
        <button type="button" onClick={load} className="flex items-center gap-1.5 border border-white/10 text-[#A0A0A0] hover:text-white hover:border-white/30 rounded-lg px-3 py-2 text-xs"><RefreshCw size={12} /> Refresh</button>
      </div>

      {loading ? (
        <div className="text-[#A0A0A0] text-sm py-16 text-center">Loading…</div>
      ) : !data ? (
        <div className="text-[#A0A0A0] text-sm py-16 text-center">No data.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Redemptions" value={data.summary.redemption_count} />
            <StatCard label="Points Redeemed" value={(data.summary.points_redeemed || 0).toLocaleString("en-IN")} accent="text-[#D78CFF]" />
            <StatCard label="OTP Bypassed" value={data.summary.bypassed_count} accent={data.summary.bypassed_count > 0 ? "text-[#F59E0B]" : "text-white"} />
            <StatCard label="Staff-performed" value={data.summary.staff_performed_count} accent={data.summary.staff_performed_count > 0 ? "text-[#F59E0B]" : "text-white"} />
          </div>

          {data.summary.staff_performed_count > 0 && (
            <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg px-4 py-2.5 text-xs text-[#F59E0B]">
              {data.summary.staff_performed_count} redemption{data.summary.staff_performed_count !== 1 ? "s were" : " was"} performed on a staff session. Only the venue admin should hold the override PIN — consider rotating it.
            </div>
          )}

          <Panel title="Redemptions">
            {data.rows.length === 0 ? (
              <div className="text-[#A0A0A0] text-sm py-4 text-center">No redemptions in this period.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[#555]">
                      <th className="text-left font-semibold pb-2">Date</th>
                      <th className="text-left font-semibold pb-2">Customer</th>
                      <th className="text-right font-semibold pb-2">Points</th>
                      <th className="text-left font-semibold pb-2 pl-3">Bill</th>
                      <th className="text-left font-semibold pb-2 pl-3">Performed by</th>
                      <th className="text-left font-semibold pb-2 pl-3">OTP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r, i) => {
                      const isStaff = r.performed_by_role === "staff";
                      return (
                        <tr key={i} className={`border-t border-white/5 ${isStaff ? "border-l-2 border-l-[#F59E0B] bg-[#F59E0B]/5" : ""}`}>
                          <td className="py-2 pr-2 text-[#A0A0A0] whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                          <td className="py-2 pr-2 text-white">{r.customer_name || "—"}</td>
                          <td className="py-2 pr-2 text-[#D78CFF] font-bold text-right">{r.delta}</td>
                          <td className="py-2 pr-2 pl-3 text-[#A0A0A0] font-mono text-xs">{r.bill_number || "—"}</td>
                          <td className="py-2 pr-2 pl-3 text-[#A0A0A0]">
                            {r.performed_by || "—"}
                            {r.performed_by_role && <span className={`ml-1 text-[10px] ${isStaff ? "text-[#F59E0B]" : "text-[#555]"}`}>({r.performed_by_role})</span>}
                          </td>
                          <td className="py-2 pl-3">
                            {r.otp_bypassed ? (
                              <span className="text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 border bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/40">OTP bypassed</span>
                            ) : r.otp_verified ? (
                              <span className="text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 border bg-emerald-500/10 text-emerald-300/80 border-emerald-500/30">verified</span>
                            ) : (
                              <span className="text-[#555]">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}

// ── Audit tab — high-exposure check actions (Prompt U, Commit 4) ──────────────
// The owner-facing read over the append-only check_events log: voids, reopens, discounts, comps and
// no-payment closes. A bill voided after pocketing the cash is the oldest theft in hospitality; the
// deterrent is the owner being able to see it.
const AUDIT_EVENT_LABEL = {
  bill_voided: "Void", bill_reopened: "Reopen", discount_applied: "Discount",
  line_comped: "Comp", marked_unpaid: "Closed unpaid",
  customer_attached: "Customer added", customer_cleared: "Customer removed",   // corrections (AA-7)
};
function AuditTab() {
  const [from, setFrom] = useState(todayIST());
  const [to, setTo] = useState(todayIST());
  const [evt, setEvt] = useState("");   // "" = whole family
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from, to };   // AA-7: standardised on from/to
      if (evt) params.event = evt;
      const { data: d } = await api.get("/reports/check-events", { params });
      setData(d);
    } catch (err) { toast.error(formatError(err)); setData(null); }
    finally { setLoading(false); }
  }, [from, to, evt]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
        <span className="text-[#A0A0A0] text-sm">to</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
        <select value={evt} onChange={(e) => setEvt(e.target.value)} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
          <option value="">All actions</option>
          <option value="bill_voided">Voids</option>
          <option value="bill_reopened">Reopens</option>
          <option value="discount_applied">Discounts</option>
          <option value="line_comped">Comps</option>
          <option value="marked_unpaid">Closed unpaid</option>
          <option value="customer_attached">Customer added</option>
          <option value="customer_cleared">Customer removed</option>
        </select>
        <button type="button" onClick={load} className="flex items-center gap-1.5 border border-white/10 text-[#A0A0A0] hover:text-white hover:border-white/30 rounded-lg px-3 py-2 text-xs"><RefreshCw size={12} /> Refresh</button>
      </div>

      {loading ? (
        <div className="text-[#A0A0A0] text-sm py-16 text-center">Loading…</div>
      ) : !data ? (
        <div className="text-[#A0A0A0] text-sm py-16 text-center">No data.</div>
      ) : (
        <Panel title="Voids, reopens, discounts & comps">
          {data.events.length === 0 ? (
            <div className="text-[#A0A0A0] text-sm py-4 text-center">No such actions in this period.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#555]">
                    <th className="text-left font-semibold pb-2">When</th>
                    <th className="text-left font-semibold pb-2 pl-3">Action</th>
                    <th className="text-left font-semibold pb-2 pl-3">Bill</th>
                    <th className="text-right font-semibold pb-2 pl-3">Amount</th>
                    <th className="text-left font-semibold pb-2 pl-3">By</th>
                    <th className="text-left font-semibold pb-2 pl-3">Reason</th>
                    <th className="text-left font-semibold pb-2 pl-3">KOT</th>
                  </tr>
                </thead>
                <tbody>
                  {data.events.map((e, i) => {
                    const isVoid = e.event === "bill_voided";
                    return (
                      <tr key={i} className={`border-t border-white/5 ${isVoid ? "border-l-2 border-l-red-500 bg-red-500/5" : ""}`}>
                        <td className="py-2 pr-2 text-[#A0A0A0] whitespace-nowrap">{fmtDateTime(e.created_at)}</td>
                        <td className="py-2 pr-2 pl-3 text-white">{AUDIT_EVENT_LABEL[e.event] || e.event}{e.item_name ? ` · ${e.item_name}` : ""}</td>
                        <td className="py-2 pr-2 pl-3 text-[#A0A0A0] font-mono text-xs">{e.check_number != null ? `C${e.check_number}` : "—"}</td>
                        <td className="py-2 pr-2 pl-3 text-right text-[#E0E0E0]">{e.amount != null ? `₹${Number(e.amount).toFixed(2)}` : "—"}</td>
                        <td className="py-2 pr-2 pl-3 text-[#A0A0A0]">{e.actor_name || "—"}</td>
                        <td className="py-2 pr-2 pl-3 text-[#A0A0A0]">{e.reason || "—"}</td>
                        <td className="py-2 pl-3">{e.kot_printed ? <span className="text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 border bg-amber-500/15 text-amber-300 border-amber-500/40">printed</span> : <span className="text-[#555]">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}

// ── Order Sales ───────────────────────────────────────────────────────────────
// Confirmed status vocabularies (do not invent values):
const OS_PAYMENT_STATUSES = ["unpaid", "pending", "paid", "refund_initiated", "refunded", "expired"];
const OS_REFUND_STATUSES = ["initiated", "refunded", "failed", "stuck"];
const OS_ORDER_TYPES = ["takeaway", "dine_in", "room"];
const OS_PAYMENT_METHODS = ["cash", "online", "upi", "card"];

function OrderSalesTab() {
  const [from, setFrom] = useState(todayIST());
  const [to, setTo] = useState(todayIST());
  const [orderType, setOrderType] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [refundStatus, setRefundStatus] = useState("");
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Undefined filters are dropped by axios — shared by the fetch and the CSV export.
  const params = useMemo(() => ({
    from, to,
    order_type: orderType || undefined,
    payment_method: paymentMethod || undefined,
    payment_status: paymentStatus || undefined,
    refund_status: refundStatus || undefined,
    include_cancelled: includeCancelled,
  }), [from, to, orderType, paymentMethod, paymentStatus, refundStatus, includeCancelled]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/order-sales", { params });
      setData(d);
    } catch (err) { toast.error(formatError(err)); setData(null); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const ctl = "bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white";
  const s = data?.summary;

  const refundCell = (rs) => {
    if (!rs) return <span className="text-[#555]">—</span>;
    const base = "text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 border";
    if (rs === "failed") return <span className={`${base} bg-red-500/15 text-red-400 border-red-500/50`}>failed</span>;
    if (rs === "stuck") return <span className={`${base} bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/50`}>stuck</span>;
    if (rs === "refunded") return <span className={`${base} bg-emerald-500/10 text-emerald-300/80 border-emerald-500/30`}>refunded</span>;
    return <span className="text-[#A0A0A0]">{rs}</span>;  // initiated
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={ctl} />
        <span className="text-[#A0A0A0] text-sm">to</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={ctl} />
        <select value={orderType} onChange={(e) => setOrderType(e.target.value)} className={ctl}>
          <option value="">All types</option>
          {OS_ORDER_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", "-")}</option>)}
        </select>
        <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className={ctl}>
          <option value="">All methods</option>
          {OS_PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} className={ctl}>
          <option value="">All payment statuses</option>
          {OS_PAYMENT_STATUSES.map((p) => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
        </select>
        <select value={refundStatus} onChange={(e) => setRefundStatus(e.target.value)} className={ctl}>
          <option value="">All refunds</option>
          {OS_REFUND_STATUSES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-[#A0A0A0] cursor-pointer">
          <input type="checkbox" checked={includeCancelled} onChange={(e) => setIncludeCancelled(e.target.checked)} className="accent-[#9D00FF]" />
          Include cancelled
        </label>
        <button type="button" onClick={load} className="flex items-center gap-1.5 border border-white/10 text-[#A0A0A0] hover:text-white hover:border-white/30 rounded-lg px-3 py-2 text-xs"><RefreshCw size={12} /> Refresh</button>
        <button type="button" onClick={() => downloadCsv("/reports/export/order-sales.csv", params, `tabon-order-sales-${from}-to-${to}.csv`)}
          className="flex items-center gap-1.5 border border-white/10 text-[#A0A0A0] hover:text-white hover:border-white/30 rounded-lg px-3 py-2 text-xs"><Download size={12} /> Export CSV</button>
      </div>

      {loading ? (
        <div className="text-[#A0A0A0] text-sm py-16 text-center">Loading…</div>
      ) : !data ? (
        <div className="text-[#A0A0A0] text-sm py-16 text-center">No data.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Orders" value={s.count} />
            <StatCard label="Gross" value={money(s.gross)} accent="text-emerald-400" />
            <StatCard label="Refunded" value={money(s.refunded_amount)} accent={s.refunded_count > 0 ? "text-[#F59E0B]" : "text-white"} />
            <StatCard label="Net" value={money(s.net)} />
          </div>

          {data.truncated && (
            <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg px-4 py-2.5 text-xs text-[#F59E0B]">
              Showing the most recent {data.row_cap} of {s.count} orders. Narrow the date range, or export the CSV for the full list.
            </div>
          )}

          <Panel title="Orders">
            {data.rows.length === 0 ? (
              <div className="text-[#A0A0A0] text-sm py-4 text-center">No orders in this period.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[#555]">
                      <th className="text-left font-semibold pb-2">Order #</th>
                      <th className="text-left font-semibold pb-2 pl-3">Date</th>
                      <th className="text-left font-semibold pb-2 pl-3">Type</th>
                      <th className="text-left font-semibold pb-2 pl-3">Customer</th>
                      <th className="text-right font-semibold pb-2 pl-3">Amount</th>
                      <th className="text-left font-semibold pb-2 pl-3">Method</th>
                      <th className="text-left font-semibold pb-2 pl-3">Instrument</th>
                      <th className="text-left font-semibold pb-2 pl-3">Payment ref</th>
                      <th className="text-left font-semibold pb-2 pl-3">Payment status</th>
                      <th className="text-left font-semibold pb-2 pl-3">Refund status</th>
                      <th className="text-right font-semibold pb-2 pl-3">Refund amt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r, i) => {
                      const flag = r.refund_status === "failed" ? "border-l-2 border-l-red-500 bg-red-500/5"
                        : r.refund_status === "stuck" ? "border-l-2 border-l-[#F59E0B] bg-[#F59E0B]/5" : "";
                      return (
                        <tr key={i} className={`border-t border-white/5 ${flag}`}>
                          <td className="py-2 pr-2 text-white font-semibold whitespace-nowrap">#{r.order_number}</td>
                          <td className="py-2 pr-2 pl-3 text-[#A0A0A0] whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                          <td className="py-2 pr-2 pl-3 text-[#A0A0A0]">{(r.order_type || "").replace("_", "-") || "—"}</td>
                          <td className="py-2 pr-2 pl-3 text-white">{r.customer_name || "—"}</td>
                          <td className="py-2 pr-2 pl-3 text-white text-right">{money(r.total)}</td>
                          <td className="py-2 pr-2 pl-3 text-[#A0A0A0]">{r.payment_method || "—"}</td>
                          <td className="py-2 pr-2 pl-3 text-[#A0A0A0] whitespace-nowrap">{r.instrument_detail || "—"}</td>
                          <td className="py-2 pr-2 pl-3 text-[#A0A0A0] font-mono text-xs">{r.payment_reference || "—"}</td>
                          <td className="py-2 pr-2 pl-3 text-[#A0A0A0]">{(r.payment_status || "").replace(/_/g, " ")}</td>
                          <td className="py-2 pr-2 pl-3">{refundCell(r.refund_status)}</td>
                          <td className="py-2 pl-3 text-right text-[#F59E0B]">{r.refund_amount != null ? money(r.refund_amount) : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-white/10 font-bold">
                      <td className="py-2 pr-2 text-white" colSpan={4}>Totals ({s.count})</td>
                      <td className="py-2 pr-2 pl-3 text-right text-emerald-400">{money(s.gross)}</td>
                      <td colSpan={4}></td>
                      <td className="py-2 pr-2 pl-3 text-right text-[#A0A0A0]">{s.refunded_count} refund{s.refunded_count !== 1 ? "s" : ""}</td>
                      <td className="py-2 pl-3 text-right text-[#F59E0B]">{money(s.refunded_amount)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function Reports() {
  const { user, hasFeature } = useAuth();
  const ordering = hasFeature("ordering");
  const loyalty = hasFeature("loyalty_points");
  const roomServiceEnabled = user?.venue?.room_service_enabled === true;
  // Day Summary is ordering-only (non-ordering venues only see the minority of
  // bills captured for loyalty, so a revenue summary would be misleading).
  const [tab, setTab] = useState(ordering ? "day" : "redemptions");

  const tabs = [
    ...(ordering ? [{ key: "day", label: "Day Summary" }] : []),
    { key: "order-sales", label: "Order Sales" },  // always visible — not feature-gated
    ...(ordering ? [{ key: "items", label: "Item Sales" }] : []),
    ...(ordering && roomServiceEnabled ? [{ key: "room", label: "Room Service" }] : []),
    ...(loyalty ? [{ key: "redemptions", label: "Redemptions" }] : []),
    ...(ordering ? [{ key: "audit", label: "Audit" }] : []),
  ];

  return (
    <AppLayout>
      <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-5">
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">Reports</h1>
          <p className="text-sm text-[#A0A0A0]">{ordering
            ? "Sales, cash reconciliation, item performance, and redemptions."
            : "Points redemptions and staff accountability."}</p>
        </div>

        <div className="flex items-center gap-1 border-b border-white/10 mb-5 overflow-x-auto">
          {tabs.map((t) => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-semibold transition border-b-2 -mb-px whitespace-nowrap ${
                tab === t.key ? "border-[#9D00FF] text-white" : "border-transparent text-[#A0A0A0] hover:text-white"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "day" && ordering && <DaySummaryTab />}
        {tab === "order-sales" && <OrderSalesTab />}
        {tab === "items" && ordering && <ItemSalesTab />}
        {tab === "room" && ordering && roomServiceEnabled && <RoomServiceTab />}
        {tab === "redemptions" && loyalty && <RedemptionsTab />}
        {tab === "audit" && ordering && <AuditTab />}
      </div>
    </AppLayout>
  );
}
